import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowLeft, Gift, ChevronDown, ChevronUp } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import UpsellCard from '../components/UpsellCard';
import { usePageView } from '../hooks/useAnalytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Cart = () => {
  const { 
    cart, 
    removeFromCart, 
    updateQuantity, 
    clearCart, 
    getTotalPrice,
    selectedUpsells,
    toggleUpsell,
    isUpsellSelected,
    getUpsellsTotal,
    getGrandTotal
  } = useCart();
  
  const [upsells, setUpsells] = useState([]);
  const [showUpsells, setShowUpsells] = useState(true);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  // Track page view
  usePageView('cart');

  useEffect(() => {
    fetchUpsells();
    fetchSettings();
  }, []);

  const fetchUpsells = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/public/upsells`);
      if (response.ok) {
        const data = await response.json();
        setUpsells(data.filter(u => u.is_active));
      }
    } catch (error) {
      console.error('Error fetching upsells:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const getWhatsAppNumber = () => {
    return settings?.whatsapp_number || settings?.footer?.whatsapp || '5543996963570';
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const handleCheckout = () => {
    const phoneNumber = getWhatsAppNumber();
    let message = `*Pedido MULTICELL*\n\n`;
    
    // Produtos
    message += `*PRODUTOS:*\n`;
    cart.forEach((item, index) => {
      const title = item.title || `${item.brand} ${item.model}`;
      message += `${index + 1}. ${title}\n`;
      
      // Adiciona cor se existir
      if (item.selectedVariant?.name) {
        message += `   Cor: ${item.selectedVariant.name}\n`;
      }
      
      // Adiciona armazenamento se existir
      if (item.storage) {
        message += `   Armazenamento: ${item.storage}\n`;
      }
      
      const price = item.price || item.price_pix || 0;
      message += `   Qtd: ${item.quantity} x ${formatPrice(price)}\n`;
      message += `   Subtotal: ${formatPrice(price * item.quantity)}\n\n`;
    });
    
    // Upsells (se houver)
    if (selectedUpsells.length > 0) {
      message += `*ADICIONAIS:*\n`;
      selectedUpsells.forEach(upsell => {
        message += `+ ${upsell.title} — ${formatPrice(upsell.price)}\n`;
      });
      message += '\n';
    }
    
    // Totais
    message += `━━━━━━━━━━━━━━━━━━━━\n`;
    message += `*Produtos:* ${formatPrice(getTotalPrice())}\n`;
    if (selectedUpsells.length > 0) {
      message += `*Adicionais:* ${formatPrice(getUpsellsTotal())}\n`;
    }
    message += `*TOTAL: ${formatPrice(getGrandTotal())}*\n\n`;
    message += `Gostaria de finalizar este pedido!`;
    
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  // Helper para obter imagem do item
  const getItemImage = (item) => {
    // Se tem variante com imagem, usa ela
    if (item.selectedVariant?.image_url) {
      return item.selectedVariant.image_url;
    }
    // Senão usa imagem principal
    return item.image_url || (item.images && item.images[0]) || null;
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[#0F0F10]">
        <div className="bg-[#1C1C1F] border-b border-[#2A2A2D] py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-2xl md:text-4xl font-bold text-[#EAEAEA]">Carrinho de Compras</h1>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl p-8 md:p-12 text-center">
            <ShoppingBag className="h-16 w-16 md:h-24 md:w-24 mx-auto text-[#3A3A3D] mb-6" />
            <h2 className="text-xl md:text-2xl font-bold text-[#EAEAEA] mb-4">
              Seu carrinho está vazio
            </h2>
            <p className="text-[#B5B5B5] mb-8">
              Adicione produtos ao carrinho para continuar suas compras
            </p>
            <Link
              to="/catalogo"
              className="inline-block bg-[#C9A24D] hover:bg-[#E0B85C] text-[#0F0F10] px-6 md:px-8 py-3 rounded-lg font-bold transition-all"
              data-testid="go-to-catalog-btn"
            >
              Ver Catálogo
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F0F10]">
      {/* Header */}
      <div className="bg-[#1C1C1F] border-b border-[#2A2A2D] py-6 md:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/catalogo" 
            className="inline-flex items-center space-x-2 text-[#C9A24D] hover:text-[#E0B85C] mb-3 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Continuar comprando</span>
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold text-[#EAEAEA]">Carrinho de Compras</h1>
          <p className="text-[#B5B5B5] mt-1 text-sm">{cart.length} {cart.length === 1 ? 'item' : 'itens'}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          
          {/* Cart Items + Upsells */}
          <div className="lg:col-span-2 space-y-6">
            {/* Cart Items */}
            <div className="space-y-3">
              {cart.map((item) => {
                const price = item.price || item.price_pix || 0;
                const itemImage = getItemImage(item);
                
                return (
                  <div 
                    key={item.cartItemId} 
                    className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl p-4"
                    data-testid={`cart-item-${item.cartItemId}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Product Image */}
                      <Link to={`/produto/${item.id}`} className="flex-shrink-0">
                        {itemImage ? (
                          <img
                            src={itemImage}
                            alt={item.title || `${item.brand} ${item.model}`}
                            className="w-20 h-20 object-contain rounded-lg bg-[#0F0F10] p-2 border border-[#2A2A2D]"
                          />
                        ) : (
                          <div className="w-20 h-20 bg-[#0F0F10] rounded-lg flex items-center justify-center border border-[#2A2A2D]">
                            <ShoppingBag className="h-6 w-6 text-[#3A3A3D]" />
                          </div>
                        )}
                      </Link>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <Link to={`/produto/${item.id}`}>
                          <h3 className="text-[#EAEAEA] font-semibold text-sm md:text-base truncate hover:text-[#C9A24D] transition-colors">
                            {item.title || `${item.brand} ${item.model}`}
                          </h3>
                        </Link>
                        
                        {/* Variant Info */}
                        <div className="flex flex-wrap gap-2 mt-1">
                          {item.selectedVariant?.name && (
                            <span className="inline-flex items-center gap-1.5 text-xs text-[#B5B5B5]">
                              <span 
                                className="w-3 h-3 rounded-full border border-[#3A3A3D]" 
                                style={{ backgroundColor: item.selectedVariant.hex || '#333' }}
                              />
                              {item.selectedVariant.name}
                            </span>
                          )}
                          {item.storage && (
                            <span className="text-xs text-[#B5B5B5]">
                              {item.storage}
                            </span>
                          )}
                        </div>
                        
                        <p className="text-[#C9A24D] font-bold text-lg mt-1">
                          {formatPrice(price)}
                        </p>
                        
                        {/* Quantity Controls - Mobile */}
                        <div className="flex items-center justify-between mt-3 md:hidden">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)}
                              className="w-8 h-8 bg-[#2A2A2D] text-[#EAEAEA] rounded-lg flex items-center justify-center"
                              disabled={item.quantity <= 1}
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                            <span className="text-[#EAEAEA] font-bold w-8 text-center">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                              className="w-8 h-8 bg-[#2A2A2D] text-[#EAEAEA] rounded-lg flex items-center justify-center"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                          <button
                            onClick={() => removeFromCart(item.cartItemId)}
                            className="text-red-400 hover:text-red-300 p-2"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </div>

                      {/* Quantity Controls - Desktop */}
                      <div className="hidden md:flex items-center gap-3">
                        <button
                          onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)}
                          className="w-9 h-9 bg-[#2A2A2D] hover:bg-[#3A3A3D] text-[#EAEAEA] rounded-lg flex items-center justify-center transition-colors"
                          disabled={item.quantity <= 1}
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <span className="text-[#EAEAEA] font-bold text-lg w-8 text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                          className="w-9 h-9 bg-[#2A2A2D] hover:bg-[#3A3A3D] text-[#EAEAEA] rounded-lg flex items-center justify-center transition-colors"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Remove Button - Desktop */}
                      <button
                        onClick={() => removeFromCart(item.cartItemId)}
                        className="hidden md:flex text-red-400 hover:text-red-300 transition-colors p-2"
                        data-testid={`remove-item-${item.cartItemId}`}
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Subtotal */}
                    <div className="mt-3 pt-3 border-t border-[#2A2A2D] flex justify-between items-center">
                      <span className="text-[#666] text-sm">Subtotal:</span>
                      <span className="text-[#EAEAEA] font-bold">
                        {formatPrice(price * item.quantity)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Clear Cart Button */}
            <button
              onClick={clearCart}
              className="text-red-400 hover:text-red-300 text-sm font-medium transition-colors"
            >
              Limpar carrinho
            </button>

            {/* Upsells Section */}
            {upsells.length > 0 && (
              <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl overflow-hidden">
                {/* Header */}
                <button
                  onClick={() => setShowUpsells(!showUpsells)}
                  className="w-full flex items-center justify-between p-4 hover:bg-[#2A2A2D]/30 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#C9A24D]/10 rounded-full flex items-center justify-center">
                      <Gift className="h-5 w-5 text-[#C9A24D]" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-[#EAEAEA] font-semibold">Adicione ao seu pedido</h3>
                      <p className="text-[#666] text-sm">Acessórios para seu smartphone</p>
                    </div>
                  </div>
                  {showUpsells ? (
                    <ChevronUp className="h-5 w-5 text-[#666]" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-[#666]" />
                  )}
                </button>

                {/* Upsell Items */}
                {showUpsells && (
                  <div className="p-4 pt-0 space-y-3" data-testid="upsells-section">
                    {upsells.map(upsell => (
                      <UpsellCard
                        key={upsell.id}
                        upsell={upsell}
                        isSelected={isUpsellSelected(upsell.id)}
                        onToggle={toggleUpsell}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Order Summary - Sticky */}
          <div className="lg:col-span-1">
            <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl p-5 lg:sticky lg:top-24">
              <h2 className="text-xl font-bold text-[#EAEAEA] mb-5">Resumo do Pedido</h2>
              
              <div className="space-y-3">
                {/* Products subtotal */}
                <div className="flex justify-between text-[#B5B5B5]">
                  <span>Produtos ({cart.length})</span>
                  <span className="font-semibold text-[#EAEAEA]">{formatPrice(getTotalPrice())}</span>
                </div>
                
                {/* Upsells subtotal (if any) */}
                {selectedUpsells.length > 0 && (
                  <div className="flex justify-between text-[#B5B5B5]">
                    <span>Adicionais ({selectedUpsells.length})</span>
                    <span className="font-semibold text-[#C9A24D]">+{formatPrice(getUpsellsTotal())}</span>
                  </div>
                )}

                {/* Selected upsells list */}
                {selectedUpsells.length > 0 && (
                  <div className="py-2 space-y-1">
                    {selectedUpsells.map(u => (
                      <div key={u.id} className="flex justify-between text-xs text-[#666]">
                        <span className="truncate pr-2">+ {u.title}</span>
                        <span>{formatPrice(u.price)}</span>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Total */}
                <div className="border-t border-[#2A2A2D] pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-[#EAEAEA]">Total</span>
                    <span className="text-2xl font-bold text-[#C9A24D]" data-testid="cart-total">
                      {formatPrice(getGrandTotal())}
                    </span>
                  </div>
                </div>
              </div>

              {/* Checkout Button */}
              <button
                onClick={handleCheckout}
                className="w-full mt-6 bg-[#25D366] hover:bg-[#20BA5A] text-white py-4 rounded-xl font-bold text-base transition-all shadow-lg hover:shadow-[0_8px_30px_rgba(37,211,102,0.3)] flex items-center justify-center gap-2"
                data-testid="checkout-button"
              >
                <svg className="h-6 w-6 fill-white" viewBox="0 0 448 512">
                  <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/>
                </svg>
                Finalizar via WhatsApp
              </button>

              <p className="text-xs text-[#666] text-center mt-4">
                Você será redirecionado para o WhatsApp para concluir sua compra
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
